import { formatPublicSourceLabel } from "@/features/machines/addressing";
import type { DashboardWidget, ResolvedWidgetValue } from "../../types";
import { resolveStopAlarm, useStopAlarm } from "../../useStopAlarm";
import { formatWidgetRelative } from "./format";
import { HeroCard, HeroMeaning, type HeroTone } from "./HeroCard";
import { describeQuality, effectiveQuality } from "./quality";

interface LedWidgetProps {
  widget: DashboardWidget;
  value: ResolvedWidgetValue;
  editable?: boolean;
  onConfigure?: () => void;
}

/**
 * Resolve o estado on/off do LED a partir de `value.numericValue` e
 * `widget.settings.activeWhen`. Quando o snapshot não está disponível,
 * retorna `null` (LED apagado).
 */
function resolveLedState(widget: DashboardWidget, value: ResolvedWidgetValue): boolean | null {
  if (!value.isAvailable) return null;

  const activeWhen = widget.settings.activeWhen ?? 1;

  if (value.numericValue === activeWhen) return true;
  if (value.numericValue === (activeWhen === 1 ? 0 : 1)) return false;
  if (value.numericValue === 1) return true;
  if (value.numericValue === 0) return false;

  if (value.value === true || value.value === "true" || value.value === "on") return true;
  if (value.value === false || value.value === "false" || value.value === "off") return false;

  return null;
}

/**
 * Widget de ESTADO no cartão herói: LED redondo + a palavra do estado como
 * herói ("Ligado" / "Desligado"), com uma linha de significado em sentence
 * case ("Saída ativa" / "Saída inativa" / estados de ausência). Substitui o
 * shell legado, que gritava "SAIDA ATIVA" / "OFFLINE" em caixa alta.
 */
export function LedWidget({ widget, value, editable, onConfigure }: LedWidgetProps) {
  const state = resolveLedState(widget, value);
  const isOn = state === true;
  const isUnavailable = state === null;
  const stateLabels = widget.settings.stateLabels ?? {
    on: "Ligado",
    off: "Desligado",
    unavailable: "Sem sinal",
  };
  const presentation = describeQuality(effectiveQuality(value));

  const label = isUnavailable
    ? stateLabels.unavailable && stateLabels.unavailable !== "Sem sinal"
      ? stateLabels.unavailable
      : presentation.title
    : isOn
      ? stateLabels.on
      : stateLabels.off;

  // Alarme de parada: desligado (off) + fresco além da tolerância.
  const stop = resolveStopAlarm(widget);
  const alarming = useStopAlarm({
    enabled: stop.enabled,
    stopped: state === false,
    fresh: !isUnavailable,
    afterMinutes: stop.afterMinutes,
  });
  const meaning: {
    tone: HeroTone;
    text: string;
    animated?: boolean;
    /** A frase so repete o valor acima — ver `HeroMeaning.redundant`. */
    redundant?: boolean;
  } = alarming
    ? { tone: "danger", text: "Desligado além do tolerado" }
    : isUnavailable
      ? resolveAbsenceMeaning(effectiveQuality(value))
      : isOn
        ? { tone: "ok", text: "Saída ativa", redundant: true }
        : { tone: "muted", text: "Saída inativa", redundant: true };

  const caption = formatPublicSourceLabel(widget.source, widget.settings.signalKind);
  const cardTone = meaning.tone === "danger" ? "danger" : meaning.tone === "warn" ? "warn" : "ok";

  return (
    <HeroCard
      title={widget.title}
      caption={caption !== widget.title ? caption : null}
      tone={cardTone}
      alarm={alarming || widget.settings.alarmEnabled}
      editable={editable}
      onConfigure={onConfigure}
      footnote={
        value.measuredAt
          ? `${isUnavailable ? "Última leitura" : "Atualizado"} ${formatWidgetRelative(value.measuredAt) ?? "agora"}`
          : null
      }
    >
      <div className="w-hero__state">
        <span className={isOn ? "led on" : "led"} aria-hidden="true" />
        <span className="w-hero__state-label">{label}</span>
      </div>

      <HeroMeaning
        tone={meaning.tone}
        animated={meaning.animated}
        redundant={meaning.redundant}
      >
        {meaning.text}
      </HeroMeaning>
    </HeroCard>
  );
}

function resolveAbsenceMeaning(
  quality: ReturnType<typeof effectiveQuality>,
): { tone: HeroTone; text: string; animated?: boolean } {
  switch (quality) {
    case "waiting":
      return { tone: "muted", text: "Dispositivo online", animated: true };
    case "stale":
      return { tone: "warn", text: "Sem leitura recente" };
    case "offline":
      return { tone: "muted", text: "Dispositivo offline" };
    default:
      return { tone: "muted", text: "Sem leitura" };
  }
}
